import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Upload as UploadIcon, CalendarDays, AlertTriangle,
  CheckCircle2, Clock, HelpCircle, Inbox, Handshake, Loader2, FileText,
  Users, MessageCircle, ArrowRight, Trash2, Download, ShieldOff,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getAllOwnership, getLetter, updateLetterStatus, deleteLetter, forgetOwnership, clearAllOwnership } from "@/lib/api";
import { toast } from "sonner";
import { RetiredNoteReply } from "@/components/RetiredNoteReply";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const FILTERS = [
  { key: "all",       label: "All letters",   icon: Inbox },
  { key: "upcoming",  label: "Upcoming",      icon: CalendarDays },
  { key: "handled",   label: "Handled",       icon: CheckCircle2 },
  { key: "needs_help",label: "Needs help",    icon: AlertTriangle },
];

const STATUS_META = {
  not_started: { label: "Not started",   className: "bg-slate-100 text-slate-700 border-slate-300",  Icon: Clock },
  accepted:    { label: "Accepted",      className: "bg-blue-50 text-blue-800 border-blue-300",       Icon: Handshake },
  in_progress: { label: "In progress",   className: "bg-amber-50 text-amber-900 border-amber-300",    Icon: Loader2 },
  completed:   { label: "Completed",     className: "bg-emerald-50 text-emerald-900 border-emerald-300", Icon: CheckCircle2 },
  needs_help:  { label: "Needs help",    className: "bg-rose-50 text-rose-900 border-rose-300",        Icon: HelpCircle },
};

function statusKey(letter) {
  const s = letter?.notifications?.status;
  if (s && STATUS_META[s]) return s;
  return "not_started";
}

function formatDeadline(analysis) {
  if (analysis?.deadline_text) return analysis.deadline_text;
  if (analysis?.deadline) {
    try {
      return new Date(analysis.deadline + "T00:00:00").toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
      });
    } catch { return analysis.deadline; }
  }
  return "No deadline";
}

function daysUntil(analysis) {
  if (!analysis?.deadline) return null;
  try {
    const d = new Date(analysis.deadline + "T00:00:00");
    const diff = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  } catch { return null; }
}

function isUpcoming(letter) {
  const s = statusKey(letter);
  if (s === "completed") return false;
  const days = daysUntil(letter?.analysis);
  return days !== null && days >= 0 && days <= 30;
}

export default function CareBinder() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const owned = getAllOwnership();
      if (owned.length === 0) {
        if (!cancelled) { setRows([]); setLoading(false); }
        return;
      }
      const results = await Promise.allSettled(
        owned.map((o) => getLetter(o.id, o.ownerToken).then((letter) => ({ ...o, letter })))
      );
      if (cancelled) return;
      const ok = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);
      setRows(ok);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "handled") return rows.filter((r) => statusKey(r.letter) === "completed");
    if (filter === "needs_help") return rows.filter((r) => statusKey(r.letter) === "needs_help");
    if (filter === "upcoming") return rows.filter((r) => isUpcoming(r.letter));
    return rows;
  }, [rows, filter]);

  const counts = useMemo(() => ({
    all: rows.length,
    upcoming: rows.filter((r) => isUpcoming(r.letter)).length,
    handled: rows.filter((r) => statusKey(r.letter) === "completed").length,
    needs_help: rows.filter((r) => statusKey(r.letter) === "needs_help").length,
  }), [rows]);

  // Aggregate escalation prompts across the whole binder: letters where the current
  // active supporter is silent AND a backup exists. Amber-highlight when 48h+ has elapsed.
  const pendingNudges = useMemo(() => {
    const items = [];
    for (const r of rows) {
      const n = r.letter?.notifications || {};
      const cc = n.care_circle || [];
      if (cc.length < 2) continue;
      const activeSlot = n.active_slot || 1;
      const active = cc.find((m) => m.slot === activeSlot);
      const nextBackup = cc.find((m) => m.slot === activeSlot + 1);
      if (!active || !nextBackup) continue;
      if (active.confirmed_at) continue;
      if (statusKey(r.letter) === "completed") continue;
      const notifiedAt = active.notified_at || active.email_sent_at;
      const hours = notifiedAt
        ? Math.max(0, (Date.now() - new Date(notifiedAt).getTime()) / 3_600_000)
        : 0;
      items.push({
        id: r.id,
        docType: r.letter?.analysis?.document_type || "Letter",
        activeName: active.name,
        backupName: nextBackup.name,
        hours,
        past48h: hours >= 48,
      });
    }
    return items;
  }, [rows]);

  return (
    <>
      <Header />
      <main data-testid="care-binder-page" className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-center gap-3">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-moss-700 text-bone shadow-sm">
            <BookOpen strokeWidth={2.5} className="h-7 w-7" />
          </span>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900">Your Care Binder</h1>
            <p className="mt-1 text-lg text-slate-700">Every letter you&apos;ve handled with LetterLifts, all in one calm place.</p>
          </div>
        </div>

        {/* Escalation banner — aggregated across the whole binder */}
        {pendingNudges.length > 0 && (
          <section
            className={`mt-8 rounded-3xl border-2 p-5 sm:p-6 ${
              pendingNudges.some((p) => p.past48h) ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white"
            }`}
            data-testid="care-binder-nudge-banner"
          >
            <div className="flex items-start gap-3">
              <span className={`grid h-11 w-11 place-items-center rounded-2xl flex-shrink-0 ${
                pendingNudges.some((p) => p.past48h) ? "bg-amber-700 text-bone" : "bg-moss-700 text-bone"
              }`}>
                <Users strokeWidth={2.5} className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
                  {pendingNudges.length === 1
                    ? "1 letter is waiting on a reply"
                    : `${pendingNudges.length} letters are waiting on a reply`}
                </h2>
                <ul className="mt-3 space-y-2" data-testid="pending-nudges-list">
                  {pendingNudges.map((p) => {
                    const daysLabel = p.hours >= 24
                      ? `${Math.floor(p.hours / 24)} day${Math.floor(p.hours / 24) === 1 ? "" : "s"}`
                      : `${Math.floor(p.hours)} hour${Math.floor(p.hours) === 1 ? "" : "s"}`;
                    return (
                      <li key={p.id} data-testid={`nudge-item-${p.id}`}>
                        <Link
                          to={`/letter/${p.id}/action?nudge=1`}
                          data-testid={`nudge-link-${p.id}`}
                          className="group flex items-center justify-between gap-3 rounded-2xl border-2 border-transparent bg-white px-4 py-3 hover:border-moss-700 transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="text-base font-semibold text-slate-900 truncate">
                              <span className="text-slate-500">On</span> {p.docType}
                            </p>
                            <p className="mt-0.5 text-sm text-slate-700">
                              {daysLabel} since <b>{p.activeName || "your contact"}</b> was reached{p.past48h ? " — time to nudge" : ""} <b>{p.backupName}</b>.
                            </p>
                          </div>
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-bone flex-shrink-0 ${
                              p.past48h ? "bg-amber-700 group-hover:bg-amber-800" : "bg-moss-700 group-hover:bg-moss-800"
                            }`}
                          >
                            Nudge {p.backupName}
                            <ArrowRight strokeWidth={2.5} className="h-4 w-4" />
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Filters */}
        <div className="mt-8 flex flex-wrap gap-2" role="tablist" data-testid="care-binder-filters">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-3 text-base font-semibold transition-colors ${
                  active
                    ? "border-moss-700 bg-moss-700 text-bone"
                    : "border-slate-300 bg-white text-slate-800 hover:border-moss-700 hover:text-moss-700"
                }`}
              >
                <Icon strokeWidth={2.5} className="h-5 w-5" />
                <span>{f.label}</span>
                <span
                  className={`ml-1 rounded-full px-2 py-0.5 text-xs font-bold ${
                    active ? "bg-bone/20 text-bone" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {counts[f.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="mt-8">
          {loading ? (
            <div className="rounded-3xl border-2 border-slate-200 bg-white p-10 text-center" data-testid="care-binder-loading">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-moss-700" />
              <p className="mt-3 text-lg text-slate-700">Gathering your letters...</p>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState kind="none" />
          ) : filtered.length === 0 ? (
            <EmptyState kind="filter" filter={filter} />
          ) : (
            <ul className="space-y-4" data-testid="care-binder-list">
              {filtered.map((r) => (
                <LetterRow
                  key={r.id}
                  row={r}
                  onMarkedDone={(id, status, status_updated_at) => {
                    setRows((prev) => prev.map((x) => x.id === id
                      ? {
                          ...x,
                          letter: {
                            ...x.letter,
                            notifications: {
                              ...(x.letter?.notifications || {}),
                              status,
                              status_updated_at,
                              supporter_confirmed_at: x.letter?.notifications?.supporter_confirmed_at || status_updated_at,
                            },
                          },
                        }
                      : x));
                  }}
                  onDeleted={(id) => {
                    setRows((prev) => prev.filter((x) => x.id !== id));
                  }}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Privacy zone — visible only when there's at least one letter */}
        {rows.length > 0 && !loading && (
          <PrivacyZone rowCount={rows.length} onWiped={() => setRows([])} />
        )}
      </main>
      <Footer />
    </>
  );
}

function LetterRow({ row, onMarkedDone, onDeleted }) {
  const { id, ownerToken, letter } = row;
  const a = letter?.analysis || {};
  const s = statusKey(letter);
  const meta = STATUS_META[s];
  const StatusIcon = meta.Icon;
  const days = daysUntil(a);
  const urgent = a.urgency === "high" || (days !== null && days >= 0 && days <= 7);
  const seniorName = letter?.notifications?.senior_first_name || "";
  // Keep original array indexes so backend replies target the correct retired entry.
  const [retiredState, setRetiredState] = useState(letter?.notifications?.retired_supporters || []);
  const retired = retiredState
    .map((r, idx) => ({ ...r, __index: idx }))
    .filter((r) => r?.thank_you_message);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isDone = s === "completed";

  const markDone = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || isDone) return;
    setBusy(true);
    try {
      const r = await updateLetterStatus(id, ownerToken, "completed");
      onMarkedDone?.(id, r.status, r.status_updated_at);
      const name = seniorName || r.senior_first_name || "";
      const msg = name
        ? `Great, ${name} — this letter is marked as handled.`
        : "Great — this letter is marked as handled.";
      toast.success(msg, { duration: 5000 });
    } catch {
      toast.error("Couldn't mark this as done right now.");
    } finally { setBusy(false); }
  };

  const doExport = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Client-side JSON export. Excludes the image_base64 payload to keep the file small
    // and private-friendly (the letter data + analysis + notifications is the useful bit).
    const payload = {
      exported_at: new Date().toISOString(),
      id,
      analysis: letter?.analysis || null,
      notifications: letter?.notifications || null,
      created_at: letter?.created_at,
      expires_at: letter?.expires_at,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const slug = (a?.document_type || "letter").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    anchor.href = url;
    anchor.download = `letterlifts-${slug}-${id.slice(0, 8)}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Letter exported to your Downloads folder.");
  };

  const doDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteLetter(id, ownerToken);
      forgetOwnership(id);
      onDeleted?.(id);
      toast.success("Letter deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't delete this letter right now.");
    } finally { setDeleting(false); }
  };

  return (
    <li>
      <div
        className="group rounded-3xl border-2 border-slate-200 bg-white p-5 sm:p-6 shadow-sm hover:border-moss-700 transition-colors"
        data-testid={`care-binder-row-${id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <Link to={`/letter/${id}`} className="flex items-start gap-4 min-w-0 flex-1">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-moss-50 text-moss-700">
              <FileText strokeWidth={2.5} className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <p className="font-display text-xl sm:text-2xl font-bold text-slate-900 truncate">
                {a.document_type || "Letter"}
              </p>
              {a.sender_name ? (
                <p className="mt-0.5 text-base text-slate-700 truncate">From {a.sender_name}</p>
              ) : null}
              <p className="mt-2 flex items-center gap-2 text-base text-slate-700">
                <CalendarDays strokeWidth={2.5} className="h-4 w-4 text-slate-500" />
                <span>{formatDeadline(a)}</span>
                {days !== null && days >= 0 && days <= 30 ? (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold ${
                      urgent ? "bg-rose-100 text-rose-900" : "bg-amber-100 text-amber-900"
                    }`}
                  >
                    {days === 0 ? "Today" : days === 1 ? "1 day" : `${days} days`}
                  </span>
                ) : null}
              </p>
              {retired.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid={`retired-notes-${id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                  {retired.map((r) => (
                    <RetiredNoteReply
                      key={r.__index}
                      letterId={id}
                      ownerToken={ownerToken}
                      index={r.__index}
                      retired={r}
                      onReplied={(updated) => setRetiredState((prev) => prev.map((x, i) => i === r.__index ? { ...x, ...updated } : x))}
                      testIdPrefix={`care-reply-${id}-${r.__index}`}
                    >
                      <button
                        type="button"
                        data-testid={`retired-chip-${id}-${r.__index}`}
                        title={r.thank_you_message}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                          r.reply_sent_at
                            ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                            : "bg-moss-50 border-moss-200 text-moss-800 hover:bg-moss-100"
                        }`}
                      >
                        {r.reply_sent_at ? (
                          <CheckCircle2 strokeWidth={2.5} className="h-3.5 w-3.5" />
                        ) : (
                          <MessageCircle strokeWidth={2.5} className="h-3.5 w-3.5" />
                        )}
                        {r.reply_sent_at ? `replied to ${r.name || "earlier helper"}` : `note from ${r.name || "earlier helper"}`}
                      </button>
                    </RetiredNoteReply>
                  ))}
                </div>
              )}
            </div>
          </Link>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-bold ${meta.className}`}
              data-testid={`row-status-${id}`}
            >
              <StatusIcon strokeWidth={2.5} className={`h-4 w-4 ${s === "in_progress" ? "animate-spin" : ""}`} />
              <span>{meta.label}</span>
            </span>
            {!isDone && (
              <button
                type="button"
                onClick={markDone}
                disabled={busy}
                data-testid={`mark-as-done-btn-${id}`}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-4 py-2 text-sm font-bold text-bone hover:bg-emerald-800 disabled:opacity-60"
              >
                <CheckCircle2 strokeWidth={2.5} className="h-4 w-4" />
                {busy ? "Marking..." : "Mark as Done"}
              </button>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={doExport}
                data-testid={`export-btn-${id}`}
                className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-500"
                title="Download this letter as a JSON file"
              >
                <Download strokeWidth={2.5} className="h-3.5 w-3.5" />
                Export
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`delete-btn-${id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-50"
                  >
                    <Trash2 strokeWidth={2.5} className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`delete-dialog-${id}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this letter?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently remove <b>{a.document_type || "this letter"}</b> from LetterLifts.
                      Anyone with your family share link will see it as no longer available. This can&apos;t be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`delete-cancel-${id}`}>Keep it</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={doDelete}
                      data-testid={`delete-confirm-${id}`}
                      className="bg-rose-700 text-bone hover:bg-rose-800"
                    >
                      Yes, delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

function PrivacyZone({ rowCount, onWiped }) {
  const [wiping, setWiping] = useState(false);
  const doWipe = async () => {
    if (wiping) return;
    setWiping(true);
    try {
      const owned = getAllOwnership();
      // Best-effort delete from server; some might already be expired / TTL-purged.
      const results = await Promise.allSettled(
        owned.map((o) => deleteLetter(o.id, o.ownerToken))
      );
      clearAllOwnership();
      onWiped?.();
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        toast.warning(`Cleared this device. ${failed} letter${failed === 1 ? "" : "s"} were already gone from our server.`);
      } else {
        toast.success("This device has been cleared.");
      }
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally { setWiping(false); }
  };

  return (
    <section className="mt-12 rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="privacy-zone-card">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-slate-100 text-slate-700 flex-shrink-0">
          <ShieldOff strokeWidth={2.5} className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-2xl font-bold text-slate-900">Privacy tools</h2>
          <p className="mt-1 text-base text-slate-700">
            Sharing this computer with someone? You can wipe every letter you&apos;ve added — this removes them from our servers and from this browser.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                data-testid="wipe-device-btn"
                className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-rose-400 bg-white px-5 py-3 text-base font-bold text-rose-800 hover:bg-rose-50"
              >
                <Trash2 strokeWidth={2.5} className="h-5 w-5" />
                Delete everything on this device
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="wipe-device-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete every letter on this device?</AlertDialogTitle>
                <AlertDialogDescription>
                  You have <b>{rowCount}</b> letter{rowCount === 1 ? "" : "s"} in your Care Binder. This action permanently deletes them from LetterLifts and clears this browser. Family share links will stop working. This can&apos;t be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="wipe-cancel">Never mind</AlertDialogCancel>
                <AlertDialogAction
                  onClick={doWipe}
                  disabled={wiping}
                  data-testid="wipe-confirm"
                  className="bg-rose-700 text-bone hover:bg-rose-800"
                >
                  {wiping ? "Deleting..." : "Yes, delete everything"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </section>
  );
}

function EmptyState({ kind, filter }) {
  if (kind === "none") {
    return (
      <div className="rounded-3xl border-2 border-slate-200 bg-white p-10 text-center" data-testid="care-binder-empty">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-moss-50 text-moss-700">
          <BookOpen strokeWidth={2.5} className="h-8 w-8" />
        </span>
        <h2 className="mt-4 font-display text-2xl font-bold text-slate-900">Your binder is empty</h2>
        <p className="mt-2 text-lg text-slate-700">
          Upload your first letter and it will show up right here.
        </p>
        <Link
          to="/upload"
          data-testid="empty-upload-cta"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-moss-700 px-6 py-3 text-lg font-semibold text-bone hover:bg-moss-800 transition-colors"
        >
          <UploadIcon strokeWidth={2.5} className="h-5 w-5" />
          Upload a letter
        </Link>
      </div>
    );
  }
  const label = FILTERS.find((f) => f.key === filter)?.label || "this filter";
  return (
    <div className="rounded-3xl border-2 border-slate-200 bg-white p-10 text-center" data-testid="care-binder-filter-empty">
      <p className="text-lg text-slate-700">
        Nothing in <b>{label.toLowerCase()}</b> right now.
      </p>
    </div>
  );
}
